import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

import type { CampaignChannel, CampaignDetail } from './campaign.service';
import type { Paged } from './records-ops.service';

export type CategoryKind =
  | 'FILE_IMPORT'
  | 'MONTHLY_STATS'
  | 'MARKETING'
  | 'PROVIDER_ADDED'
  | 'MARKET_ADDED'
  | 'WELCOME_INFO';

export type AudienceScope = 'merchants' | 'users';

export interface AudienceSpec {
  scope?: AudienceScope;
  roles?: string[];
  include_notification_email?: boolean;
  merchant_ids?: string[];
  exclude_password_change_pending?: boolean;
}

export interface Choice {
  value?: string;
  label?: string;
  css?: string;
}

export interface CampaignCategory {
  id: string;
  name: string;
  code: string | null;
  description: string;
  kind: Choice | string;
  channel: Choice | string;
  processor: string;
  processor_options: Record<string, unknown>;
  template: string | null;
  sender_name: string;
  sender_email: string;
  subject: string;
  merchant_application: string | null;
  audience: AudienceSpec;
  is_active: boolean;
}

export interface CampaignSchedule {
  id: string;
  name: string;
  category: string;
  category_name: string;
  category_kind: Choice | string;
  overrides: Record<string, unknown>;
  day_of_month: number;
  run_hour_utc: number;
  is_active: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_status: string;
  last_message: string;
  last_campaign: string | null;
}

export interface ScheduleCreateInput {
  name: string;
  category: string;
  day_of_month: number;
  run_hour_utc: number;
}

export interface RescheduleInput {
  day_of_month: number;
  run_hour_utc: number;
}

/** Campaign row as returned by ``campaign-schedules/{id}/history/``. */
export interface CampaignHistoryItem {
  id: string;
  reference: string;
  name: string;
  status: Choice | string;
  total_count?: number;
  valid_count?: number;
  invalid_count?: number;
  queued_count?: number;
  sent_count?: number;
  failed_count?: number;
  delivered_count?: number;
  created_at: string;
}

export interface CampaignOverrides {
  channel?: CampaignChannel;
  processor?: string;
  processor_options?: Record<string, unknown>;
  template?: string;
  sender_name?: string;
  sender_email?: string;
  subject?: string;
  merchant_application?: string;
  audience?: AudienceSpec;
}

export interface FromAudienceParams {
  year?: number;
  month?: number;
  subject?: string;
  body_html?: string;
  heading?: string;
  cta_url?: string;
  cta_label?: string;
  entity_id?: string;
  country_id?: string;
}

export interface FromAudienceInput {
  category: string;
  name?: string;
  overrides?: CampaignOverrides;
  params?: FromAudienceParams;
  start?: boolean;
  force?: boolean;
}

export interface AudiencePreview {
  recipient_count: number;
  sample_recipients: unknown[];
  subject: string;
  html: string;
}

export interface MailingOption {
  id: string;
  name: string;
}

/** Back-office mailings: categories, schedules and audience-based campaigns (under ``auth/``). */
@Injectable({ providedIn: 'root' })
export class MailingService {
  private readonly api = inject(ApiService);

  categories(kind?: CategoryKind, activeOnly = true): Observable<Paged<CampaignCategory>> {
    const params: Record<string, string | number> = { page_size: 20 };
    if (kind) params['kind'] = kind;
    if (activeOnly) params['is_active'] = 'true';
    return this.api.get<Paged<CampaignCategory>>('auth/campaign-categories/', params);
  }

  schedules(): Observable<Paged<CampaignSchedule>> {
    return this.api.get<Paged<CampaignSchedule>>('auth/campaign-schedules/', { page_size: 20 });
  }

  createSchedule(body: ScheduleCreateInput): Observable<CampaignSchedule> {
    return this.api.post<CampaignSchedule>('auth/campaign-schedules/', { ...body });
  }

  pauseSchedule(id: string): Observable<CampaignSchedule> {
    return this.api.post<CampaignSchedule>(`auth/campaign-schedules/${id}/pause/`, {});
  }

  resumeSchedule(id: string): Observable<CampaignSchedule> {
    return this.api.post<CampaignSchedule>(`auth/campaign-schedules/${id}/resume/`, {});
  }

  rescheduleSchedule(id: string, body: RescheduleInput): Observable<CampaignSchedule> {
    return this.api.post<CampaignSchedule>(`auth/campaign-schedules/${id}/reschedule/`, {
      ...body,
    });
  }

  runScheduleNow(id: string): Observable<CampaignSchedule> {
    return this.api.post<CampaignSchedule>(`auth/campaign-schedules/${id}/run-now/`, {});
  }

  scheduleHistory(id: string): Observable<CampaignHistoryItem[]> {
    return this.api.get<CampaignHistoryItem[]>(`auth/campaign-schedules/${id}/history/`);
  }

  fromAudience(body: FromAudienceInput): Observable<CampaignDetail> {
    return this.api.post<CampaignDetail>('auth/campaigns/from-audience/', { ...body });
  }

  audiencePreview(body: FromAudienceInput): Observable<AudiencePreview> {
    return this.api.post<AudiencePreview>('auth/campaigns/audience-preview/', { ...body });
  }

  pauseCampaign(id: string): Observable<CampaignDetail> {
    return this.api.post<CampaignDetail>(`auth/campaigns/${id}/pause/`, {});
  }

  resumeCampaign(id: string): Observable<CampaignDetail> {
    return this.api.post<CampaignDetail>(`auth/campaigns/${id}/resume/`, {});
  }

  /** Financial entities matching `search` (first page, at most 20). */
  providers(search = ''): Observable<Paged<MailingOption>> {
    const params: Record<string, string | number> = { page: 1 };
    if (search) params['search'] = search;
    return this.api.get<Paged<MailingOption>>('finances/entities/', params);
  }

  /** Countries matching `search` (first page, at most 20). */
  markets(search = ''): Observable<Paged<MailingOption>> {
    const params: Record<string, string | number> = { page: 1 };
    if (search) params['search'] = search;
    return this.api.get<Paged<MailingOption>>('finances/countries/', params);
  }

  merchants(search: string): Observable<Paged<MailingOption>> {
    const params: Record<string, string | number> = { page_size: 20 };
    if (search) params['search'] = search;
    return this.api.get<Paged<MailingOption>>('auth/merchants/', params);
  }
}

/** Value of a ChoiceEnumField (`{value,label}`) or a plain string. */
export function choiceValue(value: unknown): string {
  if (value && typeof value === 'object' && 'value' in value) {
    return String((value as { value?: unknown }).value ?? '');
  }
  return typeof value === 'string' ? value : '';
}
